import { useRef } from 'react';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import { useLocation } from '@reach/router';
import { motion } from 'framer-motion';
import styled from 'styled-components';

import { LinkWithQuery } from '@/components/global/LinkWithQuery';
import { ChevronDown } from '@/icons/Icons';

import { type NavItem as NavItemType } from './config';
import { useNavDropdownContext } from './NavDropdownProvider';
import { NavMenuDropdown } from './NavMenuDropdown';

const StyledNavItem = styled(NavigationMenu.Item)`
  position: relative;
  color: ${({ theme }) => theme.fontColor.LOUD};
  font-weight: ${({ theme }) => theme.fontWeight.NORMAL};
  line-height: 1;

  & > svg {
    color: ${({ theme }) => theme.paletteCssVars.fg1200};
  }

  & > * {
    vertical-align: middle;
  }
`;

const NavItemLink = styled.a<{ 'data-dropdown'?: boolean }>`
  position: relative;
  border: 0;
  background: none;
  color: ${({ theme }) => theme.paletteCssVars.fgLoud};
  font-size: ${({ theme }) => theme.fontSize.body.base};
  line-height: ${({ theme }) => theme.lineHeight.body.base};
  font-weight: ${({ theme }) => theme.fontWeight.NORMAL};
  line-height: 1;
  display: flex;
  align-items: center;
  white-space: nowrap;
  border-radius: calc(1rem / 16);
  padding: 0.6rem 0.85rem 0.6rem 0.75rem;
  transition: none;

  &[data-dropdown] {
    padding-right: 0.6rem;
  }

  /* &[data-state='open'], */
  &:hover,
  &:focus-visible {
    background-color: ${({ theme }) => theme.paletteCssVars.fgA500};
  }

  & > svg {
    margin-right: 0.5rem;
    color: ${({ theme }) => theme.paletteCssVars.fg1200};
  }
`;

const ChevronContainer = styled.div`
  margin-left: 0.35rem;
  color: ${({ theme }) => theme.paletteCssVars.fg1200};
`;

export function NavMenuItem({ item }: { item: NavItemType }) {
  const ref = useRef<HTMLButtonElement>(null);
  const state = useNavDropdownContext();
  const isOpen = state.value && ref.current?.id.includes(state.value);
  const { pathname } = useLocation();

  switch (true) {
    case 'dropdown' in item:
      return (
        <StyledNavItem>
          <NavigationMenu.Trigger asChild>
            <NavItemLink
              ref={ref}
              as={motion.button}
              data-dropdown
              whileTap={{ scale: 0.96 }}>
              {item.icon} {item.title}
              <ChevronContainer
                animate={isOpen ? 'open' : 'closed'}
                as={motion.div}
                style={{ display: 'block' }}
                variants={{
                  open: {
                    rotate: -180
                  },
                  closed: {
                    rotate: 0
                  }
                }}>
                <ChevronDown />
              </ChevronContainer>
            </NavItemLink>
          </NavigationMenu.Trigger>
          <NavMenuDropdown item={item} />
        </StyledNavItem>
      );

    case 'to' in item:
      return (
        <StyledNavItem>
          <NavigationMenu.Link active={pathname === item.to} asChild>
            <NavItemLink as={LinkWithQuery} to={item.to}>
              {item.icon} {item.title}
            </NavItemLink>
          </NavigationMenu.Link>
        </StyledNavItem>
      );

    default:
      return null;
  }
}
